import { ChangeDetectionStrategy, Component, ChangeDetectorRef, input, output, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { S25ToggleButtonModule } from "../../s25-toggle-button/s25.toggle.button.module";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { Item } from "../../../pojo/Item";
import { fetchResourceResultsFromSearch, type ResourceSearchResult } from "./object.occurrence.availability";
import { OBJECT_OCCURRENCE_SEARCH_LAYOUT_STYLES, ObjectOccurrenceSearchBase } from "./object.occurrence.search.base";
import { S25Util } from "../../../util/s25-util";

export type { SearchItem } from "./object.occurrence.search.base";

export type { ResourceSearchResult } from "./object.occurrence.availability";

@TypeManagerDecorator("s25-ng-object-occurrence-resource-search")
@Component({
    selector: "s25-ng-object-occurrence-resource-search",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [FormsModule, S25DropdownPaginatedModule, S25ToggleButtonModule, S25ButtonComponent],
    template: `
        <div class="resource-selection-per-occurrence">
            <div class="search-controls">
                <div class="search-method-selector">
                    <span class="search-method-label">Resource search</span>
                    <s25-toggle-button
                        [value]="searchMethod() === 'name'"
                        (valueChange)="onSearchMethodChange($event ? 'name' : 'saved')"
                        [falseLabel]="'Saved search'"
                        [trueLabel]="'Search by name'"
                        [toggleLabel]="'Resource search method'"
                    />
                </div>

                <div class="add-quantity-row">
                    <label class="add-quantity-label">
                        Qty when adding
                        <input
                            type="number"
                            class="c-input resource-add-qty-input"
                            min="1"
                            step="1"
                            [ngModel]="addResourceQuantity()"
                            (ngModelChange)="onAddResourceQuantityInput($event)"
                        />
                    </label>
                </div>

                @if (searchMethod() === "saved") {
                    <div class="search-dropdown-section">
                        <label>Select Saved Search:</label>
                        <s25-ng-search-dropdown
                            [itemTypeId]="Item.Ids.Resource"
                            [onSelect]="onSearchSelected"
                            [value]="selectedSearch()"
                        >
                        </s25-ng-search-dropdown>
                        @if (selectedSearch() !== undefined) {
                            <s25-ng-button [type]="'outline'" [onClick]="clearSearchSelection" class="clear-search-btn">
                                Clear
                            </s25-ng-button>
                        }
                    </div>
                }

                @if (searchMethod() === "name") {
                    <div class="search-text-section">
                        <label>Enter Resource Name:</label>
                        <div class="text-search-input-group">
                            <input
                                type="text"
                                class="c-input text-search-input"
                                [ngModel]="nameSearchText()"
                                (ngModelChange)="nameSearchText.set($event)"
                                (keyup.enter)="searchByName()"
                                placeholder="Enter resource name..."
                            />
                            <s25-ng-button [type]="'primary'" [onClick]="searchByName" class="search-btn"
                                >Search</s25-ng-button
                            >
                        </div>
                    </div>
                }
            </div>

            @if (searching()) {
                <div class="search-loading">Searching resources...</div>
            }

            @if (resourceResults().length > 0) {
                <div class="resource-results">
                    <h5>Available resources</h5>
                    <ul class="search-results-list">
                        @for (row of resourceResults(); track row.resourceId) {
                            <li class="search-result-item">
                                <div class="resource-result-info">
                                    <span class="resource-name">{{ row.resourceName }}</span>
                                    <span class="availability-badge"
                                        >({{ row.availableCount }}/{{ totalOccurrences() }})</span
                                    >
                                </div>
                                @if (row.availableOccurrenceUuids.includes(occurrenceUuid())) {
                                    <s25-ng-button
                                        [type]="'primary'"
                                        [onClick]="pickResource"
                                        [onClickArgs]="[row]"
                                        class="add-resource-btn"
                                    >
                                        Add
                                    </s25-ng-button>
                                }
                            </li>
                        }
                    </ul>
                </div>
            } @else if (hasSearched() && !searching()) {
                <div class="no-results">No available resources found</div>
            }
        </div>
    `,
    styles: [
        OBJECT_OCCURRENCE_SEARCH_LAYOUT_STYLES,
        `
            .resource-selection-per-occurrence {
                margin-top: 0.75rem;
                padding: 0.75rem;
                background-color: #f9fff0;
                border-radius: 4px;
                border: 1px solid #c8e6c9;
            }
            .resource-result-info {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 0.35rem;
            }
            .add-quantity-row {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                flex-wrap: wrap;
            }
            .add-quantity-label {
                font-weight: 500;
                font-size: 0.9rem;
            }
            .resource-add-qty-input {
                width: 4.5rem;
            }
        `,
    ],
})
export class S25ObjectOccurrenceResourceSearchComponent extends ObjectOccurrenceSearchBase {
    readonly resourcePicked = output<{ resourceId: number; resourceName: string; actRsrcCount: number }>();

    readonly Item = Item;

    readonly addResourceQuantity = signal(1);

    readonly resourceResults = signal<ResourceSearchResult[]>([]);

    constructor(cd: ChangeDetectorRef) {
        super(cd);
    }

    protected override resetSearchResults(): void {
        this.resourceResults.set([]);
    }

    protected override rewriteSavedSearchQuery(searchQuery: string): string {
        return searchQuery.replace("&resources_", "&");
    }

    protected override async runSearch(searchQuery: string): Promise<void> {
        this.searching.set(true);
        this.hasSearched.set(false);
        try {
            const rows = await fetchResourceResultsFromSearch(
                searchQuery,
                this.occurrences(),
                this.existingObjectIds(),
                this.addResourceQuantity(),
            );
            this.resourceResults.set(rows);
            this.hasSearched.set(true);
        } catch (e) {
            console.error("Resource search failed", e);
            this.resourceResults.set([]);
            this.hasSearched.set(true);
        } finally {
            this.searching.set(false);
            this.cd.markForCheck();
        }
    }

    protected override emptyNameSearchAlert(): string {
        return "Please enter a resource name to search";
    }

    protected override buildNameSearchQuery(trimmedName: string): string {
        return `&resource_name=${encodeURIComponent(trimmedName)}`;
    }

    readonly onAddResourceQuantityInput = (raw: string | number) => {
        this.addResourceQuantity.set(S25Util.clamp(S25Util.parseInt(raw), 1, Infinity));
    };

    readonly pickResource = (row: ResourceSearchResult) => {
        if (!row.availableOccurrenceUuids.includes(this.occurrenceUuid())) {
            return;
        }
        this.resourcePicked.emit({
            resourceId: row.resourceId,
            resourceName: row.resourceName,
            actRsrcCount: this.addResourceQuantity(),
        });
        this.resourceResults.update((list) => list.filter((r) => r.resourceId !== row.resourceId));
        this.cd.markForCheck();
    };
}
